import { useEffect, useState } from "react";
import { useToast } from "@chakra-ui/react";
import useCompanyProcess from "./useCompanyProcess";
import useDefaultPage from "@shared/hooks/useDefaultPage";
import isBaseQueryError from "@shared/helpers/isBaseQueryError";
import useCompany from "@companies/hooks/useCompany";
import type {CompanyProcess, CompanyProcessValues} from "@companyProcess/types/companyProcess";
import { Company  } from "@companies/types/company";
import { useGetCompanyProcessByCompanyQuery } from "@companyProcess/services/companyProcessService";
import useToggle from "@shared/hooks/useToggle";

const useCompanyProcessPage = () => {
    const toast = useToast()
    const [isLogsOpen, setIsLogsOpen] = useToggle(false)
    const {companies} = useCompany()
    const {
        createCompanyProcess,
        updateCompanyProcess,
        deleteCompanyProcess,
        updateCompanyProcessStatus
    } = useCompanyProcess()
    const {
        selectedItem,
        isOpenDrawer,
        isOpenDialog,
        onCloseDilog,
        setIsOpenDrawer,
        onPressEditItem,
        onPressAddItem,
        onPressDeleteItem
    } = useDefaultPage<CompanyProcess>()
    const [selectedCompany, setSelectedCompany] = useState<Company | undefined>(undefined)
    const {data: companyProcess} = useGetCompanyProcessByCompanyQuery(selectedCompany?.id.toString() ?? '', {skip: !selectedCompany})

    useEffect(() => {
        if(companies?.length && !selectedCompany){
            setSelectedCompany(companies[0])
        }
    }, [companies, selectedCompany])

    const onCreateCompanyProcess = async(values: CompanyProcessValues) => {
        const result = await createCompanyProcess({
            empresaId: values.empresaId,
            nombre: values.nombre,
            horaInicio: values.horaInicio,
            horaFin: values.horaFin,
            lunes: values.lunes,
            martes: values.martes,
            miercoles: values.miercoles,
            jueves: values.jueves,
            viernes: values.viernes,
            sabado: values.sabado,
            domingo: values.domingo,
            detenido: Number(values.detenido),
            activo: Number(values.activo)
        })

        if(result.isErr()){
            toast({
                title: 'Crear proceso de empresa',
                description: isBaseQueryError(result.error) ? result.error.message : 'Error al crear proceso de empresa',
                status: 'error',
                duration: 9000,
                isClosable: true
            })

            return false
        }

        toast({
            title: 'Crear proceso de empresa',
            description: 'Proceso de empresa creado',
            status: 'success',
            duration: 9000,
            isClosable: true
        })

        return true
    }

    const onUpdateCompanyProcess = async(id: number, values:CompanyProcessValues) => {
        const result = await updateCompanyProcess({
            id,
            empresaId: values.empresaId,
            nombre: values.nombre,
            horaInicio: values.horaInicio,
            horaFin: values.horaFin,
            lunes: values.lunes,
            martes: values.martes,
            miercoles: values.miercoles,
            jueves: values.jueves,
            viernes: values.viernes,
            sabado: values.sabado,
            domingo: values.domingo,
            detenido: Number(values.detenido),
            activo: Number(values.activo)
        })

        if(result.isErr()){
            toast({
                title: 'Actualizar proceso de empresa',
                description: isBaseQueryError(result.error) ? result.error.message : 'Error al actualizar proceso de empresa',
                status: 'error',
                duration: 9000,
                isClosable: true
            })

            return false
        }

        toast({
            title: 'Actualizar proceso de empresa',
            description: 'Proceso de empresa actualizado',
            status: 'success',
            duration: 9000,
            isClosable: true
        })
        return true
    }

    const onDeleteCompanyProcess = async() => {
        if(!selectedItem) return
        const result = await deleteCompanyProcess(selectedItem.id)

        if(result.isErr()){
            toast({
                title: 'Eliminar proceso de empresa',
                description: isBaseQueryError(result.error) ? result.error.message : 'Error al eliminar proceso de empresa',
                status: 'error',
                duration: 9000,
                isClosable: true
            })

            return
        }

        toast({
            title: 'Eliminar proceso de empresa',
            description: 'Proceso de empresa eliminado',
            status: 'success',
            duration: 9000,
            isClosable: true
        })
    }

    const onUpdateCompanyProcessStatus = async(id: string, activo: 'play' | 'stop') => {
        const result = await updateCompanyProcessStatus({id, activo, detenido: activo === 'play' ? 'play' : 'stop' })
        const title = activo === 'play' ? 'Inciar proceso' : 'Detener proceso'
        const errorMessage = activo === 'play' ? 'Error al inicial proceso' : 'Error al detener proceso'
        const successMessage = activo === 'play' ? 'Proceso iniciado' : 'Proceso detenido'

        if(result.isErr()){
            toast({
                title,
                description: isBaseQueryError(result.error) ? result.error.message : errorMessage,
                status: 'error',
                duration: 9000,
                isClosable: true
            })

            return
        }

        toast({
            title: title,
            description: successMessage,
            status: 'success',
            duration: 9000,
            isClosable: true
        })
    }

    return {
        companyProcess,
        isLogsOpen,
        companies,
        selectedItem,
        isOpenDrawer,
        selectedCompany,
        isOpenDialog,
        onCloseDilog,
        setIsOpenDrawer,
        setIsLogsOpen,
        onPressEditItem,
        onPressAddItem,
        onPressDeleteItem,
        setSelectedCompany,
        onCreateCompanyProcess,
        onUpdateCompanyProcess,
        onDeleteCompanyProcess,
        onUpdateCompanyProcessStatus
    }
}


export default useCompanyProcessPage;
